import React, { useCallback, useEffect, useMemo, useState } from "react";

const AdminFoodRecords = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [newIngredient, setNewIngredient] = useState("");
  const [newEmission, setNewEmission] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editIngredient, setEditIngredient] = useState("");
  const [editEmission, setEditEmission] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = 20;

  const token = useMemo(() => localStorage.getItem("accessToken"), []);
  const baseUrl = `${process.env.REACT_APP_API_URL}/carbonfootprint/admin`;

  const authHeaders = useMemo(
    () => ({
      "content-type": "application/json",
      authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const filteredRecords = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return records;
    }

    return records.filter((item) => {
      const ingredient = String(item.ingredient || "").toLowerCase();
      const emission = String(item.emission ?? "").toLowerCase();
      const id = String(item.id ?? "").toLowerCase();
      return ingredient.includes(query) || emission.includes(query) || id.includes(query);
    });
  }, [records, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));

  const paginatedRecords = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, currentPage]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(baseUrl, {
        method: "GET",
        headers: authHeaders,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || data?.error || "Failed to load food records");
      }

      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to load food records");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, baseUrl]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const onCreate = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const res = await fetch(baseUrl, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          ingredient: newIngredient,
          emission: Number(newEmission),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || data?.error || "Failed to add food record");
      }

      setRecords((prev) => [...prev, data].sort((a, b) => a.ingredient.localeCompare(b.ingredient)));
      setNewIngredient("");
      setNewEmission("");
    } catch (err) {
      setError(err.message || "Failed to add food record");
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditIngredient(item.ingredient || "");
    setEditEmission(String(item.emission ?? ""));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditIngredient("");
    setEditEmission("");
  };

  const saveEdit = async (id) => {
    setError("");

    try {
      const res = await fetch(`${baseUrl}/${id}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({
          ingredient: editIngredient,
          emission: Number(editEmission),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || data?.error || "Failed to update record");
      }

      setRecords((prev) =>
        prev
          .map((item) => (item.id === id ? data : item))
          .sort((a, b) => a.ingredient.localeCompare(b.ingredient))
      );
      cancelEdit();
    } catch (err) {
      setError(err.message || "Failed to update record");
    }
  };

  const deleteRecord = async (id) => {
    const shouldDelete = window.confirm("Delete this food record?");
    if (!shouldDelete) {
      return;
    }

    setError("");

    try {
      const res = await fetch(`${baseUrl}/${id}`, {
        method: "DELETE",
        headers: authHeaders,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || data?.error || "Failed to delete record");
      }

      setRecords((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      setError(err.message || "Failed to delete record");
    }
  };

  return (
    <div className="container mx-auto px-4 sm:px-8 max-w-5xl py-6">
      <h2 className="text-2xl font-bold mb-4">Food Records (Admin)</h2>

      <div className="bg-white shadow rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <input
          type="text"
          placeholder="Search by ingredient, id, or emission"
          className="border rounded px-3 py-2 md:col-span-2"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="text-sm text-gray-600 flex items-center">
          Showing {paginatedRecords.length} of {filteredRecords.length} record(s)
        </div>
      </div>

      <form onSubmit={onCreate} className="bg-white shadow rounded-lg p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          type="text"
          placeholder="Ingredient"
          className="border rounded px-3 py-2"
          value={newIngredient}
          onChange={(e) => setNewIngredient(e.target.value)}
          required
        />
        <input
          type="number"
          step="0.0001"
          placeholder="Emission"
          className="border rounded px-3 py-2"
          value={newEmission}
          onChange={(e) => setNewEmission(e.target.value)}
          required
        />
        <button type="submit" className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700">
          Add Food Record
        </button>
        <button
          type="button"
          onClick={loadRecords}
          className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
        >
          Refresh
        </button>
      </form>

      {error ? <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div> : null}

      <div className="bg-white shadow rounded-lg overflow-x-auto">
        <table className="min-w-full leading-normal">
          <thead>
            <tr>
              <th className="px-5 py-3 border-b text-left text-sm uppercase font-semibold">ID</th>
              <th className="px-5 py-3 border-b text-left text-sm uppercase font-semibold">Ingredient</th>
              <th className="px-5 py-3 border-b text-left text-sm uppercase font-semibold">Emission</th>
              <th className="px-5 py-3 border-b text-left text-sm uppercase font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && filteredRecords.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-5 py-4 text-center text-gray-500">
                  No food records found.
                </td>
              </tr>
            ) : null}

            {paginatedRecords.map((item) => {
              const isEditing = editingId === item.id;

              return (
                <tr key={item.id} className="border-b">
                  <td className="px-5 py-3">{item.id}</td>
                  <td className="px-5 py-3">
                    {isEditing ? (
                      <input
                        type="text"
                        className="border rounded px-2 py-1 w-full"
                        value={editIngredient}
                        onChange={(e) => setEditIngredient(e.target.value)}
                      />
                    ) : (
                      item.ingredient
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.0001"
                        className="border rounded px-2 py-1 w-full"
                        value={editEmission}
                        onChange={(e) => setEditEmission(e.target.value)}
                      />
                    ) : (
                      Number(item.emission).toFixed(3)
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {isEditing ? (
                      <>
                        <button
                          className="bg-green-600 text-white px-3 py-1 rounded mr-2"
                          onClick={() => saveEdit(item.id)}
                        >
                          Save
                        </button>
                        <button className="bg-gray-500 text-white px-3 py-1 rounded" onClick={cancelEdit}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="bg-indigo-600 text-white px-3 py-1 rounded mr-2"
                          onClick={() => startEdit(item)}
                        >
                          Edit
                        </button>
                        <button
                          className="bg-red-600 text-white px-3 py-1 rounded"
                          onClick={() => deleteRecord(item.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <button
          className="bg-gray-600 text-white px-4 py-2 rounded disabled:opacity-50"
          disabled={currentPage <= 1}
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
        >
          Previous
        </button>
        <span className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="bg-gray-600 text-white px-4 py-2 rounded disabled:opacity-50"
          disabled={currentPage >= totalPages}
          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AdminFoodRecords;
